import type { Optional } from '@vimeo/core/types/generic';

import { EventEmitter } from '../EventEmitter';
import { IntegrationLogger } from '../IntegrationLogger';
import type { ICrossWindowMessageData } from '../shared/crossWindowMessaging';
import {
  EIntegrationCrossWindowEvents,
  SDK_SUPPORTED_CROSS_WINDOW_EVENTS,
} from '../shared/crossWindowMessaging';

interface IConnectionConfig {
  targetOrigin: string;
  allowedOrigins: Array<string>;
}

type TMessageHandler<T> = (data: ICrossWindowMessageData<T>) => void;

export class WindowMessagingService {
  public static readonly WILDCARD_ORIGIN = '*';

  private static readonly MESSAGES_WITH_RESPONSE_TIMEOUT_MS = 3_000;

  private readonly log: IntegrationLogger = IntegrationLogger.create(
    'WindowMessagingService',
    false,
  );
  private readonly eventEmitter: EventEmitter = new EventEmitter();

  private readonly targetOrigin: string;
  private readonly allowedOrigins: Array<string>;
  private readonly acquiringTargetWindowPromise: Promise<void>;

  private targetWindow: Optional<Window> = null;
  private messageIdCounter: number = 0;
  private registeredOnMessageWithResponseEvents: Array<string> = [];

  constructor(config: IConnectionConfig) {
    this.targetOrigin = config.targetOrigin;
    this.allowedOrigins = config.allowedOrigins;
    this.validateConfig();

    this.handleNewMessage = this.handleNewMessage.bind(this);
    this.handleConnectionCheck = this.handleConnectionCheck.bind(this);
    this.setupMessageListeners();

    this.acquiringTargetWindowPromise = this.detectAndSetTargetWindow();
  }

  public destroy(): void {
    this.eventEmitter.removeAllListeners();
    this.clearMessageListeners();
    this.registeredOnMessageWithResponseEvents = [];
  }

  public async sendMessage(
    eventName: string,
    payload?: unknown,
    messageId?: string,
  ): Promise<boolean> {
    await this.acquiringTargetWindowPromise;
    if (!this.targetWindow) {
      this.log.warn(`trying to sendMessage without target window set ${eventName}`);
      return false;
    }
    const message: ICrossWindowMessageData = {
      payload,
      eventName,
      messageId,
      timestamp: Date.now(),
    };
    try {
      this.targetWindow.postMessage(message, this.targetOrigin);
      return true;
    } catch (error) {
      this.log.warn('error', `failed to postMessage ${eventName}: ${String(error)}`);
      return false;
    }
  }

  public async sendMessageWithResponse<T>(
    eventName: string,
    payload?: unknown,
  ): Promise<[isResponseReceived: true, response: T] | [isResponseReceived: false, error: Error]> {
    await this.acquiringTargetWindowPromise;
    const messageId = `${++this.messageIdCounter}_${Date.now()}`;
    return new Promise((resolve) => {
      let timeout: number | undefined;

      const responseHandler = (messageData: ICrossWindowMessageData): void => {
        if (messageId !== messageData.messageId) {
          return;
        }
        window.clearTimeout(timeout);
        resolve([true, messageData.payload as T]);
      };

      timeout = window.setTimeout(() => {
        this.offMessage(eventName, responseHandler);
        resolve([false, new Error('Response timeout')]);
      }, WindowMessagingService.MESSAGES_WITH_RESPONSE_TIMEOUT_MS);

      this.onMessage(eventName, responseHandler);

      const handleMessageSendFailed = () => {
        window.clearTimeout(timeout);
        this.offMessage(eventName, responseHandler);
        this.log.warn(`failed to postMessage (with response) ${eventName}`);
        resolve([false, new Error('Failed to postMessage')]);
      };
      this.sendMessage(eventName, payload, messageId)
        .then((isMessageSent: boolean) => {
          if (!isMessageSent) {
            handleMessageSendFailed();
          }
        })
        .catch(handleMessageSendFailed);
    });
  }

  public onMessage<T>(eventName: string, handler: TMessageHandler<T>): void {
    this.eventEmitter.on(eventName, handler);
  }

  public offMessage<T>(eventName: string, handler: TMessageHandler<T>): void {
    this.eventEmitter.off(eventName, handler);
  }

  public onMessageWithResponse<T, U>(
    eventName: string,
    handler: (messageData: ICrossWindowMessageData<T>) => U,
  ): void {
    if (this.registeredOnMessageWithResponseEvents.includes(eventName)) {
      throw new Error(
        `Trying to register more than 1 handler for message with response for event ${eventName}`,
      );
    }
    this.registeredOnMessageWithResponseEvents.push(eventName);
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.eventEmitter.on(eventName, async (messageData: ICrossWindowMessageData<T>) => {
      if (!messageData.messageId) {
        return;
      }
      try {
        const response: U = await handler(messageData);
        const isMessageSent = await this.sendMessage(eventName, response, messageData.messageId);
        if (!isMessageSent) {
          this.log.warn(
            `failed to sendMessage after onMessageWithResponse handler for event ${eventName}`,
          );
        }
      } catch (error) {
        this.log.warn(`onMessageWithResponse handler failed for event ${eventName}`, error);
      }
    });
  }

  private setupMessageListeners(): void {
    window.addEventListener('message', this.handleNewMessage);
    this.onMessageWithResponse<void, void>(
      EIntegrationCrossWindowEvents.CONNECTION_CHECK,
      this.handleConnectionCheck,
    );
  }

  private clearMessageListeners(): void {
    window.removeEventListener('message', this.handleNewMessage);
    this.offMessage(EIntegrationCrossWindowEvents.CONNECTION_CHECK, this.handleConnectionCheck);
  }

  private handleNewMessage(event: MessageEvent<ICrossWindowMessageData>): void {
    const messageData = event.data;
    const sourceWindow = event.source as Window | null;
    // only accept valid SDK messages
    if (!messageData || typeof messageData !== 'object' || !messageData.eventName) {
      return;
    }
    // ignore messages from the window we're running in
    if (sourceWindow === window) {
      return;
    }
    // ignore messages from unknown sources
    if (sourceWindow !== this.targetWindow) {
      return;
    }
    // only accept messages with known event names
    if (
      !messageData?.eventName ||
      !SDK_SUPPORTED_CROSS_WINDOW_EVENTS.includes(messageData.eventName)
    ) {
      return;
    }
    // only accept messages from allowed origins
    if (!this.isOriginAllowed(event.origin)) {
      this.log.info(`blocked inbound message from disallowed origin: ${event.origin}`);
      return;
    }
    // route to the corresponding handler
    this.eventEmitter.emit(messageData.eventName, messageData);
  }

  private handleConnectionCheck(): void {
    // do nothing, function is required to keep a reference to the handler
  }

  private async detectAndSetTargetWindow(): Promise<void> {
    await this.waitForSelfWindowLoaded();
    this.targetWindow = this.detectHostWindow();
  }

  private async waitForSelfWindowLoaded(): Promise<void> {
    if (document.readyState === 'complete') {
      return;
    }
    await new Promise((resolve) => {
      window.addEventListener('load', resolve, { once: true });
    });
  }

  private detectHostWindow(): Optional<Window> {
    const windowOpener = window.opener as Window;
    if (windowOpener) {
      this.log.info('detected host via window.opener');
      return windowOpener;
    }
    const windowParent = window.parent;
    if (windowParent && windowParent !== window) {
      this.log.info('detected host via window.parent (iframe)');
      return windowParent;
    }
    this.log.warn('no host window detected');
    return null;
  }

  private validateConfig(): void {
    if (!this.targetOrigin.length) {
      throw new Error('targetOrigin is an empty string');
    }
    if (this.allowedOrigins.length === 0) {
      throw new Error('allowedOrigins is an empty array');
    }
    const allowedWildcard = this.allowedOrigins.includes(WindowMessagingService.WILDCARD_ORIGIN);
    const targetWildcard = this.targetOrigin === WindowMessagingService.WILDCARD_ORIGIN;
    if (!allowedWildcard && !targetWildcard && !this.allowedOrigins.includes(this.targetOrigin)) {
      throw new Error(
        `targetOrigin (${
          this.targetOrigin
        }) is not in the list of allowed origins: ${this.allowedOrigins.join(',')}`,
      );
    }
  }

  private isOriginAllowed(origin: string): boolean {
    try {
      if (this.allowedOrigins.includes(WindowMessagingService.WILDCARD_ORIGIN)) {
        return true;
      }
      const hostOrigin = new URL(origin).origin;
      return this.allowedOrigins.includes(hostOrigin);
    } catch {
      return false;
    }
  }
}
